/**
 * Insights API client (E29 — Membership Intelligence).
 *
 * Typed fetchers over the read-only `/api/insights/*` surface (epic #257),
 * layered on the shared `apiRequest` from `client.ts` (inherits the resolved
 * `X-Tenant` header, `credentials: "include"`, and `ApiError.status`).
 *
 * Each frontend surface mocks the same path per-module (E29-2's
 * `composition/handlers.ts`, E29-6's `retention/handlers.ts`), so the client
 * works with no backend in dev + tests.
 *
 * This is the **canonical, complete** contract (#268): `getComposition`,
 * `getRenewals`, `getFacets`, `getTrends`, and the forward-looking `getFinancial`
 * (its `/financial` endpoint ships with #266). The next surfaces (Explorer,
 * Pulse, Trends, Financial) only IMPORT from here — they never edit this file —
 * so they never collide on it. All paths use **no trailing slash**, matching the
 * backend routes (`apps/insights/urls.py`).
 */
import { apiRequest } from "./client";
import type {
  CompositionResponse,
  FacetsQuery,
  FacetsResponse,
  FinancialResponse,
  RenewalsInsights,
  TrendsResponse,
  WinbackValuationResponse,
} from "./insights.types";

/** `GET /api/insights/composition` — the membership composition aggregate. */
export function getComposition(): Promise<CompositionResponse> {
  return apiRequest<CompositionResponse>("/insights/composition");
}

/**
 * `GET /api/insights/renewals` — the retention/renewal aggregate (PII-safe
 * counts) powering the Retention cockpit: renewal pipeline, recently-lapsed
 * win-back, license-expiry worklist, and lapse-risk bands.
 */
export function getRenewals(): Promise<RenewalsInsights> {
  return apiRequest<RenewalsInsights>("/insights/renewals");
}

/** Serialise the optional facet filter set into a `?…` query string (or ``). */
function facetsQueryString(query: FacetsQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // Drop unset fields so an empty query hits the bare path (no-op filters).
    if (value === undefined || value === null || value === "") continue;
    // `hasEmail` is a boolean tri-state → serialise as "true"/"false".
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * `GET /api/insights/facets` — faceted segment counts with live totals (PII-safe)
 * that drive the Membership Explorer's facet rail (E29-4). Each dimension is
 * counted over the members passing the supplied (optional) member-filter set, so
 * the rail can show "what's still available" counts. No trailing slash (the
 * backend route has none), mirroring `getComposition`/`getRenewals`.
 */
export function getFacets(query: FacetsQuery = {}): Promise<FacetsResponse> {
  return apiRequest<FacetsResponse>(`/insights/facets${facetsQueryString(query)}`);
}

/**
 * `GET /api/insights/trends?months=N` — the monthly new / lapsed / net /
 * cumulative-total membership series (E29-9). `months` is optional; the backend
 * clamps it to 1–60 and defaults to 12 when omitted.
 */
export function getTrends(months?: number): Promise<TrendsResponse> {
  const qs = months === undefined ? "" : `?months=${encodeURIComponent(months)}`;
  return apiRequest<TrendsResponse>(`/insights/trends${qs}`);
}

/**
 * `GET /api/insights/financial` — the Revenue Insights aggregate (E29-10):
 * dues-vs-non-dues, by-tier, PII-safe LTV buckets, renewal-revenue-at-risk, AR,
 * and a monthly trend.
 *
 * **FORWARD-LOOKING**: the endpoint does NOT exist on the merged backend yet — it
 * ships with #266. This fetcher + its types pin the epic-#257 financial contract
 * now so the Revenue surface only imports; until #266 lands the response is
 * served exclusively by the surface's own MSW mock. Figures are
 * `basis: "reported_ce21_prereconciliation"` (CE21 not yet AffiniPay-reconciled).
 */
export function getFinancial(): Promise<FinancialResponse> {
  return apiRequest<FinancialResponse>("/insights/financial");
}

/**
 * `GET /api/insights/winback-valuation` — the win-back valuation aggregate
 * (E36-7, #342): dollarizes the lapsed (90-day window) + zero-order pools via
 * each member's last-known tier fee, with the 1%/5%/10% recovery scenario
 * table the Retention cockpit's win-back valuation panel renders.
 */
export function getWinbackValuation(): Promise<WinbackValuationResponse> {
  return apiRequest<WinbackValuationResponse>("/insights/winback-valuation");
}
